"use client"

import React from "react"

import { useState, useCallback, useRef, useEffect } from "react"
import { Button } from "./button"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useCRUDController } from "./ot"
import { set } from "store"

/*
PaginationPosition: one of:
  - "top-left"
  - "top-center"
  - "top-right"
  - "bottom-left"
  - "bottom-center"
  - "bottom-right"
*/

/*
PaginatedContent props (JS version):
  - data: array of items
  - pageSize?: number (default 10)
  - initialPage?: number (default 1)
  - mode?: 'pagination' | 'scroll' (default 'pagination')
  - renderItem?: function(item, index) => ReactNode
  - className?: string
  - itemsClassName?: string
  - onPageChange?: function(page)
  - showPageInfo?: boolean
  - paginationPosition?: one of PaginationPosition strings
  - columns?: number | { sm, md, lg, xl }
  - scrollHeight?: string
  - batchRender?: function(items)
  - customComponent?: React component
  - customComponentProps?: object
  - customComponentBatch?: boolean
*/

export default function PaginatedContent({
    data,
    pageSize = 10,
    initialPage = 1,
    mode = "pagination",
    renderItem,
    className = "",
    itemsClassName = "",
    onPageChange,
    showPageInfo = true,
    paginationPosition = "bottom-center",
    columns = 1,
    scrollHeight = "auto",
    batchRender,
    customComponent: CustomComponent,
    customComponentProps = {},
    customComponentBatch = false,
    // optional total count from server (when data is a partial page)
    total,
    // optional controlled page from parent. If provided, component will sync to this page.
    currentPage: controlledCurrentPage,
    // optional reset key: when this value changes, component will reset to first page (1)
    resetKey,
}) {
    const [currentPage, setCurrentPage] = useState(
        typeof controlledCurrentPage === 'number' ? controlledCurrentPage : initialPage
    )
    const scrollRef = useRef(null)
    const observerRef = useRef(null)
    const loadMoreRef = useRef(null)

    // prefer explicit `total` if provided (server-side total count), otherwise fall back to data.length
    const totalItems = typeof total === 'number' ? total : data.length
    const totalPages = Math.ceil(totalItems / pageSize)

    const getCurrentPageData = useCallback(() => {
        if (mode === "scroll") {
            // 滾動模式：顯示從第一頁到當前頁的所有資料
            const endIndex = currentPage * pageSize
            return data.slice(0, endIndex)
        } else {
            // 分頁模式：只顯示當前頁的資料
            const startIndex = (currentPage - 1) * pageSize
            const endIndex = startIndex + pageSize
            return data.slice(startIndex, endIndex)
        }
    }, [data, currentPage, pageSize, mode])

    const currentData = getCurrentPageData()

    const hasMore = currentPage < totalPages

    const handlePageChange = useCallback(
        (newPage) => {
            if (newPage < 1 || newPage > totalPages) return
            setCurrentPage(newPage)
            onPageChange?.(newPage)

            // 分頁模式下滾動到頂部
            if (mode === "pagination" && scrollRef.current) {
                scrollRef.current.scrollIntoView({ behavior: "smooth" })
            }
        },
        [totalPages, onPageChange, mode],
    )

    // sync controlled page prop -> internal state
    useEffect(() => {
        if (typeof controlledCurrentPage === 'number' && controlledCurrentPage !== currentPage) {
            setCurrentPage(controlledCurrentPage)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlledCurrentPage])

    // resetKey: when changed, reset to first page
    useEffect(() => {
        if (typeof resetKey !== 'undefined') {
            setCurrentPage(1)
            onPageChange?.(1)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetKey])

    useEffect(() => {
        if (mode !== "scroll" || !loadMoreRef.current) return

        observerRef.current = new IntersectionObserver(
            (entries) => {
                const first = entries[0]
                if (first.isIntersecting && hasMore) {
                    handlePageChange(currentPage + 1)
                }
            },
            { threshold: 0.1 },
        )

        observerRef.current.observe(loadMoreRef.current)

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect()
            }
        }
    }, [mode, hasMore, currentPage, handlePageChange])

    const getPaginationPositionClass = () => {
        const positions = {
            "top-left": "justify-start",
            "top-center": "justify-center",
            "top-right": "justify-end",
            "bottom-left": "justify-start",
            "bottom-center": "justify-center",
            "bottom-right": "justify-end",
        }
        return positions[paginationPosition]
    }

    const isPaginationTop = paginationPosition.startsWith("top-")

    const getGridClass = () => {
        if (typeof columns === "number") {
            return `grid-cols-${columns}`
        } else {
            const classes = ["grid"]
            if (columns.sm) classes.push(`grid-cols-${columns.sm}`)
            if (columns.md) classes.push(`md:grid-cols-${columns.md}`)
            if (columns.lg) classes.push(`lg:grid-cols-${columns.lg}`)
            if (columns.xl) classes.push(`xl:grid-cols-${columns.xl}`)
            return classes.join(" ")
        }
    }

    const renderPaginationButtons = () => (
        <div className={`flex items-center gap-2 ${getPaginationPositionClass()}`}>
            <Button
                variant="outline"
                size="icon"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
            >
                <ChevronLeft className="h-4 w-4" />
            </Button>

            <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    // 顯示策略：第一頁、最後一頁、當前頁及其前後頁
                    const showPage = page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)

                    if (!showPage) {
                        // 顯示省略號
                        if (page === currentPage - 2 || page === currentPage + 2) {
                            return (
                                <span key={page} className="px-2 text-muted-foreground">
                                    ...
                                </span>
                            )
                        }
                        return null
                    }

                    return (
                        <Button
                            key={page}
                            variant={page === currentPage ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(page)}
                            className="min-w-[2.5rem]"
                        >
                            {page}
                        </Button>
                    )
                })}
            </div>

            <Button
                variant="outline"
                size="icon"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
            >
                <ChevronRight className="h-4 w-4" />
            </Button>
        </div>
    )

    const renderContent = () => {
        // 使用自定義元件
        if (CustomComponent) {
            if (customComponentBatch) {
                // 批次渲染：傳入所有當前頁資料
                return <CustomComponent data={currentData} {...customComponentProps} />
            } else {
                // 逐項渲染
                return (
                    <div className={itemsClassName || `grid gap-4 ${getGridClass()}`}>
                        {currentData.map((item, index) => (
                            <CustomComponent key={index} data={item} {...customComponentProps} />
                        ))}
                    </div>
                )
            }
        }

        // 使用 batchRender
        if (batchRender) {
            return <div>{batchRender(currentData)}</div>
        }

        // 使用 renderItem
        if (renderItem) {
            return (
                <div className={itemsClassName || `grid gap-4 ${getGridClass()}`}>
                    {currentData.map((item, index) => (
                        <div key={index}>{renderItem(item, index)}</div>
                    ))}
                </div>
            )
        }

        return null
    }

    return (
        <div className={`flex flex-col gap-4 ${className}`} ref={scrollRef}>
            {showPageInfo && (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                        顯示 {mode === "pagination" ? (currentPage - 1) * pageSize + 1 : 1} -{" "}
                        {Math.min(currentPage * pageSize, data.length)} 共 {totalItems} 項
                    </span>
                    <span>
                        {mode === "scroll" ? `已載入 ${currentPage}` : `第 ${currentPage}`} / {totalPages} 頁
                    </span>
                </div>
            )}

            {mode === "pagination" && isPaginationTop && renderPaginationButtons()}

            <div
                className={mode === "scroll" ? "overflow-y-auto" : ""}
                style={mode === "scroll" ? { maxHeight: scrollHeight } : undefined}
            >
                {renderContent()}

                {mode === "scroll" && (
                    <div ref={loadMoreRef} className="flex items-center justify-center py-4">
                        {hasMore ? (
                            <div className="text-sm text-muted-foreground">載入更多...</div>
                        ) : (
                            <div className="text-sm text-muted-foreground">已載入全部內容</div>
                        )}
                    </div>
                )}
            </div>

            {mode === "pagination" && !isPaginationTop && renderPaginationButtons()}
        </div>
    )
}
//動態拿資料
export const DaynamicFetchDataPaginatedContent = ({
    apiRoute,
    params,
    PaginatedContentProps
}) => {

    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(
        {
            cur_page: 1,
            size: 10,
        }
    );
    const dataController = useCRUDController({
        apiRoute,
    })
    const getData = (dataParams, type) => {
        setLoading(true);
        dataController.getData({
            ...dataParams
        }).then((res) => {

            let data = res.data || [];
            let total = res?.total || 0;
            if (type === "pageChange") {
                if (PaginatedContentProps?.mode === "scroll") {
                    setData((pre) => {
                        return [...pre, ...data];
                    })
                }
                else {
                    setData(data);
                }
            }
            else if (type === "paramChange") {
                //參數改變，重置資料
                setData(data);
            }
            setTotal(total);

        }).catch((e) => {
            console.error('request error', e);
        }).finally(() => {
            setLoading(false);
        });
    }
    useEffect(() => {
        setPage({
            cur_page: 1,
            size: 10
        })
        getData({
            ...params,
            cur_page: 1,
            size: 10
        }, 'paramChange')
    }, [params]);
    useEffect(() => {
        getData({
            ...params,
            ...page,
        }, 'pageChange')
    }, [page]);
    return (
        <PaginatedContent

            {...PaginatedContentProps}
            currentPage={page}
            onPageChange={(page) => {
                setPage(page)
            }}
            data={data}
            total={total}
        />
    )
}


export const StaticDataPaginatedContent = ({
    items,
    params,
    PaginatedContentProps
}) => {

    const [data, setData] = useState(items || []);
    const [total, setTotal] = useState(items?.length || 0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(
        {
            cur_page: 1,
            size: 10,
        }
    );
    useEffect(() => {
        setData(items || []);
        setTotal(items?.length || 0);
        setPage({
            cur_page: 1,
            size: 10
        })
    }, [items]);
    

    return (
        <PaginatedContent

            {...PaginatedContentProps}
            currentPage={page}
            onPageChange={(page) => {
                setPage(page)
            }}
            data={data}
            total={total}
        />
    )
}
